// Browser-safe by construction: zod only. Values here are mirrored into
// packages/web/src/model/middleware-types.ts — update both in the same change.
import { z } from 'zod'

export const PLACE_ID_IDENTIFIER_SYSTEM = 'https://behuman.health/google-place-id'
export const NPI_IDENTIFIER_SYSTEM = 'http://hl7.org/fhir/sid/us-npi'
export const FACILITY_ENRICHMENT_CODE_SYSTEM = 'https://behuman.health/resource-type'
export const FACILITY_ENRICHMENT_CODE = 'facility-enrichment'
export const FACILITY_ENRICHMENT_PAYLOAD_URL =
  'https://behuman.health/fhir/StructureDefinition/facility-enrichment'
// Prefix minted for a legacy/seeded Organization's placeId when Places is
// unavailable (facility-discovery.service.ts legacySearch) — it has no real
// Google place id. adopt() checks this prefix to skip the Places lookup.
export const LEGACY_PLACE_ID_PREFIX = 'org:'

export function normalizeFax(value: string | null | undefined): string | undefined {
  let digits = (value ?? '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1)
  if (digits.length !== 10) return undefined
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const DISCOVERY_KINDS = ['radiology', 'lab'] as const
export type DiscoveryKind = (typeof DISCOVERY_KINDS)[number]

export const LAB_VENDORS = ['labcorp', 'quest'] as const
export type LabVendor = (typeof LAB_VENDORS)[number]

// The parent lab Organization's exact name — a lab location is tied to its
// vendor only through `partOf` (type=laboratory&name:exact=…), never a code.
export const LAB_VENDOR_PARENT_NAME: Record<LabVendor, string> = {
  labcorp: 'Labcorp',
  quest: 'Quest Diagnostics',
}
export const LAB_VENDOR_QUERY: Record<LabVendor, string> = {
  labcorp: 'Labcorp patient service center',
  quest: 'Quest Diagnostics patient service center',
}
// Google answers a brand query with the brand's competitors too, so results are
// filtered by name. Labcorp's own signage is inconsistent ("LabCorp", "Lab Corp").
const LAB_VENDOR_NAME_PATTERN: Record<LabVendor, RegExp> = {
  labcorp: /labcorp|lab ?corp/i,
  quest: /quest/i,
}
export function labVendorMatches(name: string, vendor: LabVendor): boolean {
  return LAB_VENDOR_NAME_PATTERN[vendor].test(name)
}

export const BEHUMAN_ORG_TYPE_SYSTEM = 'https://behuman.health/organization-type'
export const LAB_FACILITY_CODE = 'laboratory-location'
export const RADIOLOGY_FACILITY_CODE = 'radiology-facility'

// Structural rather than a FHIR import so this file stays zod-only.
export function hasBehumanOrgType(
  org: { type?: { coding?: { system?: string; code?: string }[] }[] },
  code: string,
): boolean {
  return (
    org.type?.some((t) =>
      t.coding?.some((c) => c.system === BEHUMAN_ORG_TYPE_SYSTEM && c.code === code),
    ) ?? false
  )
}

export const RADIOLOGY_DISCOVERY_MODALITIES = [
  'mri',
  'ct_scan',
  'xray',
  'ultrasound',
  'womens_health',
  'pet_scan',
  'nuclear_medicine',
  'osteoporosis_detection',
] as const
export type DiscoveryModality = (typeof RADIOLOGY_DISCOVERY_MODALITIES)[number]

export const DEFAULT_PLACES_QUERY = 'diagnostic imaging radiology center'
export const MODALITY_QUERY: Record<DiscoveryModality, string> = {
  mri: 'MRI imaging center',
  ct_scan: 'CT scan imaging center',
  xray: 'X-ray imaging center',
  ultrasound: 'ultrasound imaging center',
  womens_health: 'mammography breast imaging center',
  pet_scan: 'PET scan imaging center',
  nuclear_medicine: 'nuclear medicine imaging center',
  osteoporosis_detection: 'DEXA bone density scan center',
}

export function isDiscoveryModality(value: unknown): value is DiscoveryModality {
  return (RADIOLOGY_DISCOVERY_MODALITIES as readonly string[]).includes(value as string)
}

/**
 * Free text first so Google weights the facility name, then the kind's phrase.
 * A lab search is a brand search, so the vendor phrase replaces the modality one.
 */
export function placesQueryFor(
  opts: { kind?: string; vendor?: LabVendor; modality?: string; query?: string } = {},
): string {
  const phrase =
    opts.kind === 'lab' && opts.vendor
      ? LAB_VENDOR_QUERY[opts.vendor]
      : isDiscoveryModality(opts.modality)
        ? MODALITY_QUERY[opts.modality]
        : DEFAULT_PLACES_QUERY
  const text = opts.query?.trim()
  return text ? `${text} ${phrase}` : phrase
}

export const NPPES_STATUS_VALUES = ['pending', 'matched', 'possible', 'none', 'failed'] as const
export type NppesStatus = (typeof NPPES_STATUS_VALUES)[number]
export const AI_STATUS_VALUES = ['pending', 'ready', 'failed'] as const
export type AiStatus = (typeof AI_STATUS_VALUES)[number]

export const NppesEnrichmentSchema = z.object({
  status: z.enum(NPPES_STATUS_VALUES),
  npi: z.string().optional(),
  taxonomy: z.string().optional(),
  legalName: z.string().optional(),
  phone: z.string().optional(),
  fax: z.string().optional(),
  // The matched NPI-2 record's practice-location address — lets a caller confirm the
  // match is the same building (referral-discovery's fax address guard) rather than
  // just the same name/ZIP.
  street: z.string().optional(),
  zip: z.string().optional(),
  checkedAt: z.string().optional(),
})
export const AiEnrichmentSchema = z.object({
  status: z.enum(AI_STATUS_VALUES),
  procedures: z.array(z.string()),
  insurances: z.array(z.string()),
  fax: z.string().optional(),
  sourceUrls: z.array(z.string()),
  checkedAt: z.string().optional(),
})
export const FacilityEnrichmentPayloadSchema = z.object({
  version: z.literal(1),
  nppes: NppesEnrichmentSchema,
  ai: AiEnrichmentSchema,
  // Which kind of search produced the `ai` block. One record per place id is shared
  // by both kinds, and a lab run deliberately writes no findings, so without this a
  // lab search would look like a finished radiology research for the next 90 days.
  // Absent on records written before labs existed — they were all radiology.
  aiKind: z.enum(DISCOVERY_KINDS).optional(),
  orderCount: z.number().int().nonnegative(),
  lastOrderedAt: z.string().optional(),
})
export type FacilityEnrichmentPayload = z.infer<typeof FacilityEnrichmentPayloadSchema>
export type NppesEnrichment = z.infer<typeof NppesEnrichmentSchema>
export type AiEnrichment = z.infer<typeof AiEnrichmentSchema>

export function emptyEnrichmentPayload(): FacilityEnrichmentPayload {
  return {
    version: 1,
    nppes: { status: 'pending' },
    ai: { status: 'pending', procedures: [], insurances: [], sourceUrls: [] },
    orderCount: 0,
  }
}
